// Logic for the minimal booking preview (/book-preview). Pure functions so they can be tested; the page only draws.
// Same server routes, same rules, same pricing as the current booking page: nothing here calculates a price.

export const MODES = { RIDE: 'ride', HOURLY: 'hourly', FAMILY: 'family' };
export const FAMILY_CLASSES = ['signature', 'majestic'];
export const SEAT_MAX = 2;                      // the server's child-seat limit
export const HOURS_MIN = 2;                     // the server's minimum for every class
export const HOURS_MAX = 12;

// Ride preferences (temperature dial, music, conversation) live in lib/cabin.js; re-exported here for the page and the older tests.
export { DEFAULT_PREFERENCES, PREFERENCE_TOGGLES, prefsSummary } from './cabin';

const pad = (n) => String(n).padStart(2, '0');

// The current New York wall clock as { date: 'YYYY-MM-DD', time: 'HH:mm' } (the server reads scheduled_date / scheduled_time as New York time).
export function nyNow(now = new Date()) {
  const f = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York', hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  const p = Object.fromEntries(f.formatToParts(now).map((x) => [x.type, x.value]));
  return { date: `${p.year}-${p.month}-${p.day}`, time: `${p.hour === '24' ? '00' : p.hour}:${p.minute}` };
}

// What the visitor picked in the date picker (their chosen wall clock is the pickup time) -> { date, time }.
export function partsFromPicker(d) {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return null;
  return { date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`, time: `${pad(d.getHours())}:${pad(d.getMinutes())}` };
}

export function whenLabel(parts) {
  if (!parts) return 'Now';
  const d = new Date(`${parts.date}T${parts.time}:00`);
  if (Number.isNaN(d.getTime())) return 'Now';
  return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} · ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
}

// The server prices by the places and the time, not by this key. Airport trips keep 'airport' (no New York City-only rule), as the Airport tab did.
export function serviceFor({ mode, pickupAirport, dropoffAirport }) {
  if (mode === MODES.HOURLY) return 'hourly';
  if (mode === MODES.FAMILY) return 'family';
  return pickupAirport || dropoffAirport ? 'airport' : 'rideNow';
}

export function classesFor(mode, all) {
  return mode === MODES.FAMILY ? all.filter((c) => FAMILY_CLASSES.includes(c.id)) : all;
}

export const effectiveHours = (hours, cls) => Math.max(Number(hours) || HOURS_MIN, HOURS_MIN, (cls && cls.minHours) || 0);

// Are both ends known well enough to ask for prices?
export function readyForClasses({ mode, pickup, dropoff }) {
  const has = (p) => !!p && !!String(p.address || '').trim() && (p.lat != null && p.lng != null || p.committed === true);
  return mode === MODES.HOURLY ? has(pickup) : has(pickup) && has(dropoff);
}

// The lines of the price breakdown, exactly as the server sent them (fare lines, then taxes and fees); lines of zero are left out. Nothing is added up here.
export const quoteBreakdown = (q) => (q ? [...(q.itemization || []), ...(q.taxLines || [])].filter((l) => l && l.label && Number(l.amount) !== 0) : []);

export const quoteTotal = (q) => (q ? Number(q.grandTotal ?? q.total ?? 0) : 0);

// The POST /api/quote body for one class.
export function quoteBody({ cls, mode, hours, seats, pickup, dropoff, when, meetGreet, pickupAirport, dropoffAirport }) {
  const body = {
    vehicle_class: cls.id,
    service: serviceFor({ mode, pickupAirport, dropoffAirport }),
    pickup_address: pickup.address, pickup_lat: pickup.lat ?? undefined, pickup_lng: pickup.lng ?? undefined,
    pickup_is_airport: !!pickupAirport, dropoff_is_airport: !!dropoffAirport,
  };
  if (mode !== MODES.HOURLY) { body.dropoff_address = dropoff.address; body.dropoff_lat = dropoff.lat ?? undefined; body.dropoff_lng = dropoff.lng ?? undefined; }
  if (mode === MODES.HOURLY) body.hours = effectiveHours(hours, cls);
  if (mode === MODES.FAMILY) body.child_seats = Math.max(0, Math.min(SEAT_MAX, Number(seats) || 0));
  if (when) body.scheduled_at = `${when.date}T${when.time}`;
  if (meetGreet) body.meet_greet = true;
  return body;
}

// The POST /api/payments/create-checkout-session body (same fields the current booking page sends).
export function checkoutBody({ cls, mode, hours, seats, pickup, dropoff, when, now, user, preferences, party, airport, total, origin, riderNote = '', noteChips = [] }) {
  const pickupAirport = airport.pickup;
  const dropoffAirport = airport.dropoff;
  const service = serviceFor({ mode, pickupAirport, dropoffAirport });
  const sched = when || now;
  const body = {
    ride_type: cls.id,
    estimated_fare: total, quoted_total: total,
    pickup_address: pickup.address,
    dropoff_address: mode === MODES.HOURLY ? ((dropoff && dropoff.address) || 'As directed') : dropoff.address,
    user_email: user.email,
    customer_name: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
    customer_phone: user.phone || '',
    scheduled_date: sched.date, scheduled_time: sched.time,
    notes: '', preferences,
    trip_type: mode === MODES.HOURLY ? 'hourly' : mode === MODES.FAMILY ? 'family' : (pickupAirport || dropoffAirport ? 'airport' : 'ride_now'),
    service,
    booking_mode: mode === MODES.HOURLY ? 'hourly' : 'point_to_point',
    hours: mode === MODES.HOURLY ? effectiveHours(hours, cls) : null,
    flight_number: pickupAirport ? (party.flightNumber || '') : '',
    airline: pickupAirport ? (party.airline || '') : '',
    passengers: party.passengers, luggage: party.bags,
    child_seats: mode === MODES.FAMILY ? Math.max(0, Math.min(SEAT_MAX, Number(seats) || 0)) : 0,
    origin_url: origin,
  };
  if (pickup.lat != null && pickup.lng != null) { body.pickup_lat = pickup.lat; body.pickup_lng = pickup.lng; }
  if (dropoff && dropoff.lat != null && dropoff.lng != null && mode !== MODES.HOURLY) { body.dropoff_lat = dropoff.lat; body.dropoff_lng = dropoff.lng; }
  if (pickupAirport && party.terminal) body.pickup_terminal = party.terminal;
  if (pickupAirport && party.meetGreet) { body.meet_greet = true; body.sign_name = party.signName; }
  if (dropoffAirport && party.dropTerminal) body.dropoff_terminal = party.dropTerminal;
  if (dropoffAirport && party.dropAirline) body.dropoff_airline = party.dropAirline;
  const trimmedNote = String(riderNote || '').trim();
  if (trimmedNote) body.rider_note = trimmedNote;
  if (Array.isArray(noteChips) && noteChips.length) body.note_chips = noteChips;
  if (party.someoneElse) { body.booked_for_name = party.passengerName; if (party.passengerPhone) body.booked_for_phone = party.passengerPhone; }
  return body;
}

// Which sections are on screen (one step at a time).
export function steps({ mode, pickup, dropoff, selectedClass, pickupAirport }) {
  const classes = readyForClasses({ mode, pickup, dropoff });
  return {
    classes,
    flight: !!pickupAirport,
    details: classes && !!selectedClass,
    style: classes && !!selectedClass && !!pickupAirport,
    seats: classes && !!selectedClass && mode === MODES.FAMILY,
  };
}

// The one optional drop-off field "Airline or terminal": a terminal ("Terminal 4", "T4", "B", "4") goes to dropoff_terminal, anything else is the airline.
export function splitDropoffNote(text) {
  const t = String(text || '').replace(/\s+/g, ' ').trim();
  if (!t) return { terminal: '', airline: '' };
  if (/^(terminal|term\.?|t)\s*[0-9a-z]{1,2}$/i.test(t) || /^[0-9a-z]$/i.test(t) || /^[0-9]{1,2}$/.test(t)) return { terminal: t.slice(0, 40), airline: '' };
  return { terminal: '', airline: t.slice(0, 60) };
}
